/**
 * Upload tour — register a tour built outside the legacy pipeline.
 *
 * The user picks the tour folder that mesh-lab produced (the one holding
 * `tourDetails.json`), names it, and the page streams every viewer asset
 * straight to Storage before asking the upload API to finalize. See
 * services/firebase/functions/tourUpload.ts for the protocol.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from 'app/constants';
import {
  abortTourUpload,
  beginTourUpload,
  collectTourFiles,
  describeTourFiles,
  finalizeTourUpload,
  uploadTourFiles,
  type FinalizeResult,
  type TourFile,
  type UploadProgress,
} from 'app/services/firebase/functions/tourUpload';
import { useAppSelector } from 'app/hooks';

type Phase =
  | { kind: 'pick' }
  | { kind: 'ready' }
  | { kind: 'uploading'; tourId: string; progress: UploadProgress | null }
  | { kind: 'finalizing'; tourId: string }
  | { kind: 'done'; result: FinalizeResult }
  | { kind: 'error'; message: string; tourId?: string };

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const card = 'rounded-2xl bg-white/80 border border-black/5 shadow-sm p-6';
const label = 'block text-sm font-medium text-black/70 mb-1.5';
const input =
  'w-full rounded-xl border border-black/10 bg-white px-4 py-2.5 text-[15px] ' +
  'outline-none focus:border-accent focus:ring-2 focus:ring-accent/20';
const primaryButton =
  'inline-flex items-center justify-center rounded-xl bg-primary px-5 py-2.5 ' +
  'text-[15px] font-medium text-white disabled:opacity-40 disabled:cursor-not-allowed';
const secondaryButton =
  'inline-flex items-center justify-center rounded-xl border border-black/10 ' +
  'bg-white px-5 py-2.5 text-[15px] font-medium text-black/75';

export default function Upload() {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.authState.user);

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const [files, setFiles] = useState<TourFile[]>([]);
  const [skipped, setSkipped] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [pickError, setPickError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'pick' });

  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // `webkitdirectory` is not in React's typings; set it on the element.
  useEffect(() => {
    const el = inputRef.current;
    if (el) {
      el.setAttribute('webkitdirectory', '');
      el.setAttribute('directory', '');
    }
  }, []);

  const summary = useMemo(() => describeTourFiles(files), [files]);
  const busy = phase.kind === 'uploading' || phase.kind === 'finalizing';

  const onPick = useCallback(
    (list: FileList | null) => {
      setPickError(null);
      if (!list || list.length === 0) return;
      try {
        const picked = collectTourFiles(list);
        setFiles(picked.files);
        setSkipped(picked.skipped);
        setTotalBytes(picked.totalBytes);
        setPhase({ kind: 'ready' });
        // Default the name to the manifest's name when the user has not typed one.
        const manifest = picked.files.find(
          (f) => f.relativePath === 'tourDetails.json',
        );
        if (manifest && !name) {
          manifest.file
            .text()
            .then((t) => JSON.parse(t))
            .then((j) => {
              if (j && typeof j.name === 'string' && j.name.trim())
                setName(j.name.trim());
              if (
                j &&
                typeof j.address === 'string' &&
                j.address.trim() &&
                !address
              )
                setAddress(j.address.trim());
            })
            .catch(() => undefined);
        }
      } catch (e) {
        setFiles([]);
        setPhase({ kind: 'pick' });
        setPickError(e instanceof Error ? e.message : String(e));
      }
    },
    [name, address],
  );

  const start = useCallback(async () => {
    if (!name.trim() || files.length === 0) return;
    const controller = new AbortController();
    abortRef.current = controller;
    let tourId: string | undefined;
    try {
      const begun = await beginTourUpload({
        name: name.trim(),
        address: address.trim(),
        isPrivate,
      });
      tourId = begun.tourId;
      setPhase({ kind: 'uploading', tourId, progress: null });
      await uploadTourFiles(begun.prefix, files, {
        signal: controller.signal,
        onProgress: (progress) =>
          setPhase({ kind: 'uploading', tourId: begun.tourId, progress }),
      });
      setPhase({ kind: 'finalizing', tourId });
      const result = await finalizeTourUpload(tourId);
      setPhase({ kind: 'done', result });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setPhase({ kind: 'error', message, tourId });
    } finally {
      abortRef.current = null;
    }
  }, [name, address, isPrivate, files]);

  const cancel = useCallback(async () => {
    abortRef.current?.abort();
    const tourId = phase.kind === 'uploading' ? phase.tourId : undefined;
    if (tourId) {
      try {
        await abortTourUpload(tourId);
      } catch {
        // The server keeps nothing the user can see until finalize; a failed
        // abort just leaves orphaned objects under a prefix nothing lists.
      }
    }
    setPhase(files.length ? { kind: 'ready' } : { kind: 'pick' });
  }, [phase, files.length]);

  const discardFailed = useCallback(async () => {
    if (phase.kind === 'error' && phase.tourId) {
      try {
        await abortTourUpload(phase.tourId);
      } catch {
        // see cancel()
      }
    }
    setPhase(files.length ? { kind: 'ready' } : { kind: 'pick' });
  }, [phase, files.length]);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 md:px-8 flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold text-primary m-0">Upload tour</h1>
        <p className="text-black/60 mt-1 mb-0">
          Add a tour that was processed outside the app. Pick the tour folder —
          the one containing{' '}
          <code className="text-[13px]">tourDetails.json</code> — and it goes
          straight into your dashboard.
        </p>
      </header>

      {/* 1. Folder */}
      <section className={card}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-base font-semibold m-0">Tour folder</h2>
            <p className="text-sm text-black/55 mt-1 mb-0">
              Only what the viewer reads is uploaded: the model, its textures,
              the panorama tiles and, when the folder has one, the 3D flythrough
              splat. Source meshes and scratch files are skipped.
            </p>
          </div>
          <label
            className={
              secondaryButton +
              ' relative' +
              (busy ? ' opacity-40 pointer-events-none' : ' cursor-pointer')
            }
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              className="sr-only"
              disabled={busy}
              onChange={(e) => onPick(e.target.files)}
            />
            {files.length ? 'Choose a different folder' : 'Choose folder…'}
          </label>
        </div>

        {pickError && (
          <p className="mt-4 mb-0 text-sm text-red-600" role="alert">
            {pickError}
          </p>
        )}

        {files.length > 0 && (
          <dl className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4 m-0">
            <div>
              <dt className="text-xs uppercase tracking-wide text-black/45">
                Floors
              </dt>
              <dd className="text-lg font-semibold m-0">{summary.floors}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-black/45">
                Panoramas
              </dt>
              <dd className="text-lg font-semibold m-0">{summary.panos}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-black/45">
                Files
              </dt>
              <dd className="text-lg font-semibold m-0">{files.length}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-black/45">
                Upload size
              </dt>
              <dd className="text-lg font-semibold m-0">
                {formatBytes(totalBytes)}
              </dd>
            </div>
            {summary.splatFiles > 0 && (
              <p className="col-span-full text-xs text-black/45 m-0">
                Includes the 3D flythrough splat ({summary.splatFiles} files).
              </p>
            )}
            {skipped > 0 && (
              <p className="col-span-full text-xs text-black/45 m-0">
                {skipped} file{skipped === 1 ? '' : 's'} the viewer does not use
                will be left out.
              </p>
            )}
          </dl>
        )}
      </section>

      {/* 2. Details */}
      <section className={card}>
        <h2 className="text-base font-semibold m-0 mb-4">Details</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={label} htmlFor="tour-upload-name">
              Name
            </label>
            <input
              id="tour-upload-name"
              className={input}
              value={name}
              maxLength={120}
              disabled={busy}
              placeholder="e.g. 12 Main St — Unit 4"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className={label} htmlFor="tour-upload-address">
              Address{' '}
              <span className="text-black/40 font-normal">(optional)</span>
            </label>
            <input
              id="tour-upload-address"
              className={input}
              value={address}
              disabled={busy}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2.5 text-sm text-black/75 sm:col-span-2">
            <input
              id="tour-upload-private"
              type="checkbox"
              checked={isPrivate}
              disabled={busy}
              onChange={(e) => setIsPrivate(e.target.checked)}
            />
            Keep this tour private
          </label>
        </div>
      </section>

      {/* 3. Go */}
      <section className={card}>
        {phase.kind === 'pick' || phase.kind === 'ready' ? (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-black/55 m-0">
              {user?.email
                ? `Uploading as ${user.email}.`
                : 'Sign in to upload.'}
            </p>
            <button
              type="button"
              className={primaryButton}
              disabled={!name.trim() || files.length === 0 || !user}
              onClick={start}
            >
              Upload {files.length ? `${files.length} files` : 'tour'}
            </button>
          </div>
        ) : null}

        {phase.kind === 'uploading' && (
          <div>
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm font-medium m-0">
                Uploading…{' '}
                {phase.progress
                  ? `${Math.round(phase.progress.fraction * 100)}%`
                  : ''}
              </p>
              <button
                type="button"
                className={secondaryButton}
                onClick={cancel}
              >
                Cancel
              </button>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-black/10">
              <div
                className="h-full rounded-full bg-accent transition-[width] duration-200"
                style={{
                  width: `${
                    phase.progress
                      ? Math.round(phase.progress.fraction * 100)
                      : 0
                  }%`,
                }}
              />
            </div>
            <p className="mt-2 mb-0 text-xs text-black/50 truncate">
              {phase.progress
                ? `${phase.progress.filesDone} of ${
                    phase.progress.filesTotal
                  } files · ${formatBytes(
                    phase.progress.bytesTransferred,
                  )} of ${formatBytes(phase.progress.totalBytes)} · ${
                    phase.progress.current
                  }`
                : 'Starting…'}
            </p>
          </div>
        )}

        {phase.kind === 'finalizing' && (
          <p className="text-sm font-medium m-0">
            Checking the upload and publishing the tour…
          </p>
        )}

        {phase.kind === 'done' && (
          <div>
            <h2 className="text-base font-semibold m-0">Tour published</h2>
            <p className="text-sm text-black/60 mt-1 mb-0">
              {phase.result.floors} floor{phase.result.floors === 1 ? '' : 's'},{' '}
              {phase.result.panos} panorama{phase.result.panos === 1 ? '' : 's'}
              .
              {phase.result.droppedPanos.length > 0 &&
                ` ${phase.result.droppedPanos.length} panorama${
                  phase.result.droppedPanos.length === 1 ? ' was' : 's were'
                } left out because their tiles were missing.`}
            </p>
            {phase.result.warnings.length > 0 && (
              <ul className="mt-3 mb-0 pl-5 text-xs text-black/55">
                {phase.result.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
            <div className="mt-4 flex gap-3 flex-wrap">
              <button
                type="button"
                className={primaryButton}
                onClick={() =>
                  navigate(
                    `${ROUTES.PERSONAL_DASHBOARD}/tour/${phase.result.tourId}`,
                  )
                }
              >
                Open tour
              </button>
              <button
                type="button"
                className={secondaryButton}
                onClick={() => {
                  setFiles([]);
                  setName('');
                  setAddress('');
                  setPhase({ kind: 'pick' });
                }}
              >
                Upload another
              </button>
            </div>
          </div>
        )}

        {phase.kind === 'error' && (
          <div>
            <h2 className="text-base font-semibold m-0 text-red-700">
              Upload failed
            </h2>
            <p className="text-sm text-black/70 mt-1 mb-0" role="alert">
              {phase.message}
            </p>
            <div className="mt-4 flex gap-3 flex-wrap">
              <button
                type="button"
                className={primaryButton}
                onClick={start}
                disabled={!files.length}
              >
                Try again
              </button>
              <button
                type="button"
                className={secondaryButton}
                onClick={discardFailed}
              >
                Discard
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
